<script setup lang="ts">
import type { ComputedRef } from 'vue'
import type { ColorVariant, ContentConversation, PersonId } from '~/models/Content/ContentConversation'
import type { TTSItem, TTSLanguage } from '~/models/Brikke/TextToSpeech'
import { computed, inject, useId, useSlots } from 'vue'
import { storeToRefs } from 'pinia'
import { faPause, faPlay, faSpinnerThird } from '@fortawesome/pro-solid-svg-icons'
import useAuthStore from '~/stores/auth'
import useLanguageMap from '~/composables/useLanguageMap'
import useImage from '~/composables/useImage'
import TextToSpeechButton from '~/components/media/TextToSpeechButton.vue'
import AudioPlayer from '~/components/media/AudioPlayer.vue'

const props = defineProps<{
  id: PersonId
  align?: 'left' | 'right'
  textToSpeech?: boolean
  language?: TTSLanguage
  audioUrl?: string
}>()

const { id, align = 'left', textToSpeech = false, audioUrl } = props

const { getImageSrc } = useImage()
const slots = useSlots()
const { languageMap } = useLanguageMap()
const { userPreferredLanguage } = storeToRefs(useAuthStore())

const conversation = inject<ContentConversation | undefined>('conversation', undefined)
const getPersonColorVariant = inject<(personId: PersonId) => ColorVariant>('getPersonColorVariant', () => 'current')
const getNextItemIndex = inject<() => number>('getNextItemIndex', () => 0)
const articleVersionNo = inject<ComputedRef<number>>('versionNo', computed(() => 0))

const person = computed(() => conversation?.persons.find(person => person.id === id))
const colorVariant = computed(() => getPersonColorVariant(id))
const isThemePop = computed(() => colorVariant.value === 'pop')

const initials = computed(() => person.value?.name
  .split(' ')
  .map(word => word[0])
  .join('')
  .toUpperCase()
  .slice(0, 2) ?? '')

const itemIndex = getNextItemIndex()
const itemKey = conversation ? `conv-${conversation.locationId}-${id}-${itemIndex}` : useId()

const ttsItem = computed((): TTSItem => {
  if (conversation) {
    return conversation
  }
  return {
    locationId: useId(),
    versionNo: articleVersionNo.value,
    title: `${props.id}-${props.align}`,
    languageCodes: [languageMap[userPreferredLanguage.value]]
  }
})
</script>

<template>
  <div
    v-if="slots.default"
    class="flex flex-col"
    :class="{
      'items-end': align === 'right',
      'items-start': align === 'left',
      'mt-4': person,
      '!mt-[2em]': !person,
    }"
  >
    <div
      v-if="person"
      class="z-10 mb-5 flex items-center gap-2"
      :class="{
        'flex-row-reverse': align === 'right',
        'ml-3': align === 'left',
        'mr-3': align === 'right',
      }"
    >
      <img
        v-if="person.image?.destinationContentId"
        :src="getImageSrc(person.image, 'thumbnail')"
        :alt="person.name"
        class="size-8 rounded-full object-cover"
      >
      <div
        v-else
        class="flex size-8 items-center justify-center rounded-full text-xs font-medium"
        :class="isThemePop ? 'bg-(--theme-pop) text-(--theme-60)' : 'bg-(--theme-20)'"
        v-text="initials"
      />
      <span class="font-medium" v-text="person.name" />
    </div>

    <div class="relative max-w-3/4">
      <div
        class="absolute -top-4 size-0 border-x-8 border-b-[1rem] border-x-transparent"
        :class="[
          isThemePop ? 'border-b-(--theme-pop)' : 'border-b-(--theme-10)',
          { 'left-5': align === 'left', 'right-5': align === 'right' },
        ]"
      />
      <div
        :id="itemKey"
        class="rounded-lg p-[0.75em]"
        :class="isThemePop ? 'bg-(--theme-pop) text-(--theme-60)' : 'bg-(--theme-10)'"
      >
        <TextToSpeechButton
          v-if="textToSpeech"
          :item="ttsItem"
          :language-code="props.language ?? null"
          :selector="`#${itemKey}`"
        >
          <template #trigger="{ activate, isPlaying, isActive, pause, generate, isLoadingTts }">
            <button
              type="button"
              class="mb-2 flex size-9 items-center justify-center rounded-sm bg-(--theme-60) text-base focus-visible:ring"
              :class="isThemePop
                ? 'text-(--theme-pop) hover:bg-(--theme-10) hover:text-(--theme-60)'
                : 'text-(--theme-10) hover:bg-(--theme-pop) hover:text-(--theme-60)'"
              @click="isPlaying ? pause() : isActive ? generate() : activate()"
            >
              <font-awesome-icon
                :icon="isLoadingTts ? faSpinnerThird : isPlaying ? faPause : faPlay"
                :spin="isLoadingTts"
                style="--fa-animation-duration: 775ms"
              />
            </button>
          </template>
        </TextToSpeechButton>
        <AudioPlayer
          v-else-if="audioUrl"
          :src="audioUrl"
          variant="button-with-text"
        >
          <template #trigger="{ activate, isPlaying, pause }">
            <button
              type="button"
              class="mb-2 flex size-9 items-center justify-center rounded-sm bg-(--theme-60) text-base focus-visible:ring"
              :class="isThemePop
                ? 'text-(--theme-pop) hover:bg-(--theme-10) hover:text-(--theme-60)'
                : 'text-(--theme-10) hover:bg-(--theme-pop) hover:text-(--theme-60)'"
              @click="isPlaying ? pause() : activate()"
            >
              <font-awesome-icon :icon="isPlaying ? faPause : faPlay" />
            </button>
          </template>
        </AudioPlayer>
        <div class="font-medium" :data-key-tts="itemKey">
          <slot />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
:deep(.translation) {
  margin-top: 0.5em;
  font-size: 0.8em;
  font-weight: normal;
}
</style>
